import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDashboardView } from '../shared/DashboardViewContext';
import ChildShellBottomNav from '../shared/ChildShellBottomNav';
import MobileChildSheets from './MobileChildSheets';
import MobileMoodFeedback from './MobileMoodFeedback';
import MobilePullRefreshIndicator from './MobilePullRefreshIndicator';
import MobileTodayCapturePanel from './MobileTodayCapturePanel';
import MobileTimelineFilterSheet from './MobileTimelineFilterSheet';
import MobileTimelinePanel from './MobileTimelinePanel';
import MobileToolsPanel from './MobileToolsPanel';
import useMobileActiveChild from './useMobileActiveChild';
import useMobileChildProfileState from './useMobileChildProfileState';
import useMobileChildSheetsState from './useMobileChildSheetsState';
import useMobileCaptureDate from './useMobileCaptureDate';
import useMobileDashboardNavigation from './useMobileDashboardNavigation';
import useMobileMoodFeedback from './useMobileMoodFeedback';
import useMobilePullToRefresh from './useMobilePullToRefresh';
import useMobileTimelineFilters from './useMobileTimelineFilters';

const MobileCaptureDashboard = ({
  children = [],
  allEntries = {},
  getUserRoleForChild,
  onRefreshDashboard,
  onQuickEntry,
  onComposerSubmit = null,
  composerSaving = false,
  composerSavingLabel = 'Saving...',
  composerSavingHelper = 'Saving your note and organizing care details...',
  onOpenChildReview,
  onEditChild,
  onDeleteChild,
  onDailyReport,
  onOpenSleepLog,
  onOpenFoodLog,
  onOpenBathroomLog,
  onOpenMedicalLog,
  onOpenIncident,
  onInviteTeamMember,
  onGoToCareTeam,
  onImportLogs,
  onAddChildClick,
  onRefreshRoles,
  loggingRemindersByChild = {},
  loggingReminderLoadingByChild = {},
  onToggleLoggingReminder,
  childShellTarget = '',
  onChildShellTargetHandled,
  initialOpenCaptureDetailsKey = '',
  initialOpenCaptureRequestKey = '',
  timelineNavigationState = null,
}) => {
  const navigate = useNavigate();
  const { activeChildId, setActiveChildId } = useDashboardView();
  const timelineRef = useRef(null);
  const timelineNavigationRequestRef = useRef('');
  const toolsRef = useRef(null);
  const { rootRef, pullDistance, isRefreshing } = useMobilePullToRefresh({ onRefreshDashboard });
  const {
    closeMobileChildSheet,
    closeSwitchMenu,
    mobileActionsSheetOpen,
    mobileSwitchSheetOpen,
    openMobileChildSheet,
    resetMobileChildSheets,
    setMobileSwitchSheetOpen,
  } = useMobileChildSheetsState({ onRefreshRoles });

  const {
    activeChild,
    activeChildAccent,
    activeChildEntries,
  } = useMobileActiveChild({ activeChildId, allEntries, children });
  const {
    captureDate,
    handleCaptureDateChange,
    resetCaptureDate,
  } = useMobileCaptureDate(activeChild?.id);
  const {
    availableTimelineTags,
    clearTimelineFilters,
    handleTimelineDatePickerClose,
    handleTimelineDatePreset,
    handleTimelineFiltersChange,
    openTimelineCalendar,
    openTimelineFilterDatePicker,
    resetTimelineFilters,
    selectedDate,
    setSelectedDate,
    timelineDatePickerAnchor,
    timelineFiltersValue,
    timelineFilterSheetOpen,
    setTimelineFilterSheetOpen,
    timelineImportantOnly,
    setTimelineImportantOnly,
    timelineTagFilters,
    timelineUserRoles,
    setTimelineUserRoles,
    toggleTimelineTagFilter,
  } = useMobileTimelineFilters({ activeChildEntries });
  const {
    activeChildCareTeam,
    activeChildRole,
    careTeamsByChildId,
  } = useMobileChildProfileState({
    activeChild,
    children,
    getUserRoleForChild,
  });
  const {
    canUndoMood,
    closeMoodModal,
    closeMoodToast,
    currentMoodLabel,
    handleMoodClear,
    handleMoodSave,
    handleUndoMood,
    moodModalOpen,
    moodToastMessage,
    moodToastOpen,
    openMoodModal,
    resetMoodFeedback,
  } = useMobileMoodFeedback({ activeChild, activeChildEntries });
  useEffect(() => {
    const handleQuickEntryDateChange = (event) => {
      const nextDate = event?.detail?.date;
      if (!(nextDate instanceof Date) || Number.isNaN(nextDate.getTime())) {
        return;
      }
      setSelectedDate(nextDate);
    };

    window.addEventListener('captureez:quick-entry-date-change', handleQuickEntryDateChange);
    return () => window.removeEventListener('captureez:quick-entry-date-change', handleQuickEntryDateChange);
  }, [setSelectedDate]);

  const handleAddTimelineEntryForDate = (date, event) => {
    if (!activeChild) {
      return;
    }
    const selectedDay = date instanceof Date && !Number.isNaN(date.getTime())
      ? date
      : selectedDate;
    const quickEntryDate = selectedDay instanceof Date && !Number.isNaN(selectedDay.getTime())
      ? new Date(selectedDay)
      : new Date();
    const now = new Date();
    quickEntryDate.setHours(now.getHours(), now.getMinutes(), 0, 0);
    onQuickEntry?.(activeChild, 'dailyLog', event, quickEntryDate);
  };
  const {
    activeShellItem,
    handleChildShellNavSelect,
    handleOpenAskSource,
    handleOpenCareKnowledge,
    handleOpenCustomPrepare,
    handleOpenIepJourney,
    handleOpenPatternsPage,
    handleReviewSourceTimeline,
    resetTimelineFocus,
    timelineFocusEntryId,
  } = useMobileDashboardNavigation({
    activeChild,
    childShellTarget,
    clearTimelineFilters,
    onChildShellTargetHandled,
    onGoToCareTeam,
    rootRef,
    setSelectedDate,
    timelineRef,
    toolsRef,
  });
  useEffect(() => {
    if (childShellTarget !== 'profile' || !activeChild || !onOpenChildReview) {
      return;
    }

    onOpenChildReview(activeChild);
    onChildShellTargetHandled?.();
  }, [activeChild, childShellTarget, onChildShellTargetHandled, onOpenChildReview]);

  useEffect(() => {
    resetTimelineFilters();
    resetMobileChildSheets();
    resetCaptureDate();
    resetTimelineFocus();
    resetMoodFeedback();
  }, [activeChild?.id, resetCaptureDate, resetMobileChildSheets, resetMoodFeedback, resetTimelineFilters, resetTimelineFocus]);

  useEffect(() => {
    const requestedDate = timelineNavigationState?.selectedDate
      ? new Date(timelineNavigationState.selectedDate)
      : null;
    const entryId = timelineNavigationState?.timelineEntryId || null;
    const navigationChildId = timelineNavigationState?.timelineChildId || null;
    const requestKey = `${navigationChildId || ''}|${timelineNavigationState?.selectedDate || ''}|${entryId || ''}`;
    if ((!requestedDate || Number.isNaN(requestedDate.getTime())) && !entryId) return;
    if (navigationChildId && navigationChildId !== activeChild?.id) return;
    if (timelineNavigationRequestRef.current === requestKey) return;
    timelineNavigationRequestRef.current = requestKey;

    handleReviewSourceTimeline({
      date: requestedDate && !Number.isNaN(requestedDate.getTime()) ? requestedDate : undefined,
      entryId,
    });
  }, [activeChild?.id, handleReviewSourceTimeline, timelineNavigationState]);

  if (!activeChild) {
    return null;
  }

  return (
    <Box
      ref={rootRef}
      sx={{
        px: { xs: 1.05, sm: 1.5 },
        pt: { xs: 1, sm: 1.25 },
        pb: 13,
        touchAction: 'pan-y',
        bgcolor: activeChildAccent.wash,
        minHeight: '100dvh',
      }}
    >
      <MobilePullRefreshIndicator
        pullDistance={pullDistance}
        isRefreshing={isRefreshing}
      />

      {activeShellItem === 'today' ? (
        <MobileTodayCapturePanel
          child={activeChild}
          initialOpenCaptureDetailsKey={initialOpenCaptureDetailsKey}
          initialOpenCaptureRequestKey={initialOpenCaptureRequestKey}
          onQuickEntry={onQuickEntry}
          onComposerSubmit={onComposerSubmit}
          composerSaving={composerSaving}
          composerSavingLabel={composerSavingLabel}
          composerSavingHelper={composerSavingHelper}
          onImportLogs={onImportLogs}
          onOpenSleepLog={onOpenSleepLog}
          onOpenFoodLog={onOpenFoodLog}
          onOpenMedicalLog={onOpenMedicalLog}
          onOpenBathroomLog={onOpenBathroomLog}
          onOpenIncident={onOpenIncident}
          onReviewAppointment={(appointmentDraft) => navigate(
            `/children/${activeChild.id}/appointments`,
            appointmentDraft?.id ? undefined : { state: { appointmentDraft } },
          )}
          canManageAppointments={['care_owner', 'care_partner'].includes(activeChildRole)}
          appointmentTimeZone={activeChild.timeZone || activeChild.timezone || ''}
          captureDate={captureDate}
          onCaptureDateChange={handleCaptureDateChange}
          loggingReminderEnabled={Boolean(loggingRemindersByChild?.[activeChild?.id])}
          loggingReminderLoading={Boolean(loggingReminderLoadingByChild?.[activeChild?.id])}
          onToggleLoggingReminder={onToggleLoggingReminder}
          onReviewTimeline={handleReviewSourceTimeline}
        />
      ) : null}

      {activeShellItem === 'tools' ? (
        <MobileToolsPanel
          ref={toolsRef}
          child={activeChild}
          accent={activeChildAccent}
          logCount={activeChildEntries.length}
          onOpenAskSource={handleOpenAskSource}
          onOpenCareKnowledge={handleOpenCareKnowledge}
          onOpenInsights={handleOpenPatternsPage}
          onOpenTherapyPrep={onDailyReport}
          onOpenCustomPrepare={handleOpenCustomPrepare}
          onOpenIepJourney={handleOpenIepJourney}
          onOpenMood={openMoodModal}
          onOpenCustomNote={onQuickEntry}
          onImportLogs={onImportLogs}
        />
      ) : null}

      {activeShellItem === 'today' ? (
        <MobileTimelinePanel
          ref={timelineRef}
          child={activeChild}
          selectedDate={selectedDate}
          filters={timelineFiltersValue}
          onFiltersChange={handleTimelineFiltersChange}
          focusEntryId={timelineFocusEntryId}
          calendarEntries={activeChildEntries}
          onDateChange={setSelectedDate}
          onOpenDatePicker={openTimelineCalendar}
          onOpenPatterns={activeChild ? () => onDailyReport?.(activeChild) : null}
          onAddEntryForDate={handleAddTimelineEntryForDate}
          onReviewAppointment={(appointmentDraft) => navigate(
            `/children/${activeChild.id}/appointments`,
            { state: { appointmentDraft } },
          )}
        />
      ) : null}

      <MobileMoodFeedback
        modalOpen={moodModalOpen}
        onModalClose={closeMoodModal}
        child={activeChild}
        currentMood={currentMoodLabel}
        onSaveMood={handleMoodSave}
        onClearMood={handleMoodClear}
        toastOpen={moodToastOpen}
        onToastClose={closeMoodToast}
        toastMessage={moodToastMessage}
        canUndo={canUndoMood}
        onUndoMood={handleUndoMood}
      />

      <ChildShellBottomNav
        activeItem={activeShellItem}
        accent={activeChildAccent}
        onSelect={handleChildShellNavSelect}
      />

      <MobileChildSheets
        actionsOpen={mobileActionsSheetOpen}
        onActionsOpen={openMobileChildSheet}
        onActionsClose={closeMobileChildSheet}
        switchOpen={mobileSwitchSheetOpen}
        onSwitchOpen={() => setMobileSwitchSheetOpen(true)}
        onSwitchClose={closeSwitchMenu}
        activeChild={activeChild}
        activeChildRole={activeChildRole}
        activeChildCareTeam={activeChildCareTeam}
        children={children}
        getUserRoleForChild={getUserRoleForChild}
        careTeamsByChildId={careTeamsByChildId}
        setActiveChildId={setActiveChildId}
        onGoToCareTeam={onGoToCareTeam}
        onEditChild={onEditChild}
        onInviteTeamMember={onInviteTeamMember}
        onDeleteChild={onDeleteChild}
        onDailyReport={onDailyReport}
        onImportLogs={onImportLogs}
        onOpenBathroomLog={onOpenBathroomLog}
        onOpenSleepLog={onOpenSleepLog}
        onAddChildClick={onAddChildClick}
      />

      <MobileTimelineFilterSheet
        open={timelineFilterSheetOpen}
        onClose={() => setTimelineFilterSheetOpen(false)}
        onClear={() => {
          clearTimelineFilters();
          setSelectedDate(new Date());
        }}
        importantOnly={timelineImportantOnly}
        onImportantOnlyChange={setTimelineImportantOnly}
        userRoles={timelineUserRoles}
        onUserRolesChange={setTimelineUserRoles}
        availableTags={availableTimelineTags}
        selectedTagKeys={timelineTagFilters}
        onToggleTag={toggleTimelineTagFilter}
        datePickerAnchor={timelineDatePickerAnchor}
        onOpenDatePicker={openTimelineFilterDatePicker}
        onCloseDatePicker={handleTimelineDatePickerClose}
        selectedDate={selectedDate}
        onSelectDate={handleTimelineDatePreset}
        entries={activeChildEntries}
      />

    </Box>
  );
};

export default MobileCaptureDashboard;
